/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import {PackageItem} from 'types/entities/PackageItem'
import {PackageItemItemType} from 'types/enums/PackageItemItemType'
import {Package} from 'types/entities/Package'
import {PackageItemPersonType} from 'types/enums/PackageItemPersonType'
import {BookingFilter, BookingProcess} from 'types/app'
import {filterPackageItemsByType, subtractDaysFromDates, uniqueFilter} from 'utils/utility'
import {PersonType} from 'types/enums/PersonType'
import {PackageCategory, ValidPackageCategory} from 'types/enums/PackageCategory'
import Dates from 'utils/Dates'
import {PackagesCart} from 'types/entities/PackagesCart'
import {VoucherDiscountType, VoucherItem} from 'types/entities/VoucherItem'
import {HallCode} from 'types/enums/HallCode'
import {GroupTypeId} from 'types/enums/GroupTypeId'
import {
	DEFAULT_HALL,
	DEFAULT_RESERVATION_DAYS_FROM_NOW,
	IS_PROD,
	MAX_MONTH_COUNT_FOR_AVAILABLE_DATES,
	SHOW_ITEM_SLOT_RANGE_ONLY,
	SHOW_ONLY_RESERVATION_START,
} from 'helpers/config'
import BookingAction, {PackageCreditItem, PackageUpsell, PackageUpsellItem} from 'store/booking/BookingAction'
import {Hall} from 'types/entities/Hall'
import {Timeslot} from 'types/entities/Timeslot'
import {ActivityType} from 'types/enums/ActivityType'
import {Activity} from 'types/entities/Activity'
import {PackagesCartItem} from 'types/entities/PackagesCartItem'
import {PackagesCartItemSlot} from 'types/entities/PackagesCartItemSlot'
import {ArticleTicketType} from 'types/enums/ArticleTicketType'
import {PackagesItemByPerson, PackagesItemsPerson, PackagesItemsPersons} from 'types/entities/PackagesItems'
import {useAppDispatch} from 'helpers/store'
import {useTypedSelector} from 'helpers/reducers'
import {addMonths} from 'date-fns'
import {PackageCategoryEntity} from 'types/entities/PackageCategoryEntity'
import {useVoucherTypeByArticle} from 'utils/voucherUtils'
import {HallOpeningHourEntries} from 'types/entities/HallOpeningHourEntries'
import {getShortNameFromId} from 'utils/reservationUtils'

export const getOfferEventCategoryShortName = (idString: string) => {
    const shortNames: Record<string, string> = {
        [PackageCategory.EventS]: 's',
        [PackageCategory.EventM]: 'm',
        [PackageCategory.EventL]: 'l',
        [PackageCategory.EventMegaFun]: 's',
        [PackageCategory.EventNonPlusUltra]: 's',
        [PackageCategory.PolterabendMegaFun]: 's',
        [PackageCategory.PolterabendNonPlusUltra]: 's',
        [PackageCategory.Kinder]: 'childrens',
        [PackageCategory.School]: 'school',
        [PackageCategory.FerienPass]: 'ferienpass',
        [PackageCategory.MegaFun]: 'mf',
        [PackageCategory.GigaFun]: 'gf',
        [PackageCategory.NonPlusUltra]: 'npu',
        [PackageCategory.HappyKids]: 's',
        [PackageCategory.HappyKidsPlus]: 's',
        [PackageCategory.HappyParty]: 's',
        [PackageCategory.HappyPartyPlus]: 's',
        [PackageCategory.HappyPartyVIP]: 's',
    }

    return shortNames[idString] || 's'
}

/* Find PackageItems by provided PackageItemPersonType
 * @param packageItem[], PackageItemPersonType
 */
export const findPackageItemByPersonType = (
    packageItems: PackageItem[],
    personType: PackageItemPersonType
): PackageItem | undefined => {
    return packageItems.find((packageItem) => {
        if (typeof packageItem.person_type === 'undefined' || packageItem.type === PackageItemItemType.Upsell)
            return false
        if (packageItem.is_active === 1) {
            return packageItem.person_type === personType
        }
        return false
    })
}

export const findPackageItemByPersonTypeAndCount = (
    packageItems: PackageItem[],
    personType: PackageItemPersonType,
    count: number
): PackageItem | undefined => {
    const packageItemsByType = packageItems.filter((packageItem) => {
        if (typeof packageItem.person_type === 'undefined') return false
        if (packageItem.type !== PackageItemItemType.PackageItem) return false
        return packageItem.person_type === personType
    })
    const packageItemByCount = packageItemsByType.find((packageItem) => {
        if (packageItem.minimum_persons_count > count) return false
        return packageItem.maximum_persons_count >= count
    })
    return packageItemByCount
}

/* Return only the items of type PackageItemItemType.PackageItem  */
export const getBookingPackageItems = (Package: Package): PackageItem[] => {
    return Package.packageItems.filter((packageItem) => packageItem.type === PackageItemItemType.PackageItem)
}

/*Compare bookingFilter to selected package requirements */
export const filterFitsWithPackageRequirements = (Package: Package | null, bookingFilter: BookingFilter): boolean => {
    let disabled = true
    let packages: PackageItem[] | false
    if (!Package) {
        return true
    }
    if (!getBookingPackageItems(Package)) {
        return true
    } else {
        packages = getBookingPackageItems(Package)
    }

    let hasEnoughAdults = false
    let hasTooMuchAdults = false

    let hasEnoughChildren = false
    let hasTooMuchChildren = false

    let hasEnoughPersonsCount = false
    let hasTooMuchPersonsCount = false
    let isBookableByHour = false

    const adultPackageItem = findPackageItemByPersonTypeAndCount(
        packages,
        PackageItemPersonType.Adult,
        bookingFilter.adults
    )
    const childPackageItem = findPackageItemByPersonTypeAndCount(
        packages,
        PackageItemPersonType.Child,
        bookingFilter.children
    )
    if (adultPackageItem) {
        //first check if there are enough adults, independent from Package type
        hasEnoughAdults = adultPackageItem.minimum_persons_count <= +bookingFilter.adults
        hasTooMuchAdults = adultPackageItem.maximum_persons_count < +bookingFilter.adults
    }

    if (childPackageItem) {
        /*first check if there are enough children, independent from Package type*/
        hasEnoughChildren = childPackageItem.minimum_persons_count <= +bookingFilter.children
        hasTooMuchChildren = childPackageItem.maximum_persons_count < +bookingFilter.children
    }

    if (adultPackageItem && childPackageItem) {
        /* both packageItems exists, bookingFilters should be counted together */
        if (Package?.min_drivers_count <= +bookingFilter.adults + bookingFilter.children) disabled = false
    }

    hasEnoughPersonsCount = Package?.min_drivers_count <= +bookingFilter.adults + bookingFilter.children
    hasTooMuchPersonsCount = Package?.max_drivers_count < +bookingFilter.adults + bookingFilter.children
    isBookableByHour = !!Package?.extra.bookableByHour

    const minDaysFromNow = Package.extra.minDaysFromNow
    if (minDaysFromNow && minDaysFromNow > 0) {
        const date1 = subtractDaysFromDates(new Date(bookingFilter.date), minDaysFromNow)
        const date2 = new Date()
        date2.setHours(0)
        date2.setMinutes(0)
        date2.setSeconds(0)
        disabled = date1 < date2
    }

    /*if packageItems has only one of personType, we take only that kind in consideration */
    if (adultPackageItem && hasEnoughAdults && childPackageItem && hasEnoughChildren) {
        disabled = false
    }

    if (adultPackageItem && childPackageItem && hasEnoughPersonsCount && Package?.min_drivers_count > 0) {
        disabled = false
    }

    if (hasTooMuchAdults || hasTooMuchChildren) {
        disabled = true
    }

    disabled = !hasEnoughPersonsCount

    if (hasTooMuchPersonsCount) {
        disabled = true
    }

    if (isBookableByHour && bookingFilter.hours_count == 0) {
        disabled = true
    }
    // console.log(hasEnoughChildren);
    // console.log(hasTooMuchAdults);
    // console.log(hasTooMuchChildren);
    // console.log("------------");
    // console.log(hasEnoughPersonsCount);
    // console.log(hasTooMuchPersonsCount);
    //
    // console.log(adultPackageItem);
    // console.log(childPackageItem);
    // console.log("disabled", disabled);
    return disabled
}

/* filter packages based on minimum and maximum drivers count compared to bookingFilter */
export const packagesFilter = (packages: Package[], bookingFilter: BookingFilter) => {
    const { adults, children, groupType, category } = bookingFilter
    const filteredPackages = Object.values(packages)
    if (filteredPackages.length === 0) return []

    const filteredCounts = filteredPackages.filter((packageRow) => {
        if (adults + children === 0) {
            return true
        }
        return packageRow.min_drivers_count <= adults + children && packageRow.max_drivers_count >= adults + children
    })

    const filteredByCategory = filteredCounts.filter((packageRow) => {
        if ((category && category === PackageCategory.All) || !category) {
            return true
        }
        if (category && category === PackageCategory.Event && packageRow.category === category) {
            return true
        }
        if (category && category === PackageCategory.Action && packageRow.category === category) {
            return true
        }
        if (
            category &&
            category === PackageCategory.Others &&
            packageRow.category !== PackageCategory.Event &&
            packageRow.category !== PackageCategory.Action
        ) {
            return true
        }
        return false
    })

    return filteredByCategory.filter((packageRow) => {
        if (packageRow.category === PackageCategory.Profile) {
            return false
        }
        if (groupType.length === 0) {
            return true
        }
        if (
            groupType.length > 0 &&
            packageRow.extra &&
            packageRow.extra.groupType &&
            packageRow.extra.groupType.length == 0
        ) {
            return false
        }
        if (packageRow.extra && packageRow.extra.groupType) {
            if (packageRow.extra.groupType.length == 0) {
                return true
            }
            if ('groupType' in packageRow.extra === false) {
                return true
            }
        }
        if (packageRow.extra && packageRow.extra.groupType) {
            if (typeof groupType == 'object') {
                return (
                    (groupType as Array<string | number>).filter((gT) => {
                        if (packageRow.extra.groupType) {
                            return packageRow.extra.groupType.includes(+gT)
                        }
                        return false
                    }).length > 0
                )
            }
            if (typeof groupType == 'string') {
                return packageRow.extra.groupType.filter((gt) => gt === +groupType).length > 0
            }
        }
        return true
    })
}

/* filter unique activities from package */
export const filteredActivities = (packageDetail: Package): number[] => {
    const activityIds = packageDetail.packageItems
        .map((packageItem) => packageItem.activity_id)
        .filter((id): id is number => id !== null)
    return uniqueFilter(activityIds)
}

/* filter unique packageItems based on activity */
export const uniquePackageWithActivityItem = (
    packageDetail: Package,
    withActivity?: boolean,
    personType: PersonType = PersonType.Adult
): PackageItem[] => {
    const filteredActivitiesArray = filteredActivities(packageDetail).filter((f) => (withActivity ? f : f === null))
    const filteredPackages = filteredActivitiesArray.map((activityNumber) => {
        const filteredPackageItems = filterPackageItemsByType(packageDetail, personType)
        const foundItem = filteredPackageItems.find((packageItem) => packageItem.activity_id === activityNumber)
        return foundItem
    })
    return filteredPackages.filter((packageItem): packageItem is PackageItem => !!packageItem) //ignore undefined and null
}

/***
 * Return upsell with extra_heat field
 * @param packageDetail
 * @param personType
 */
export const getExtraHeatPackageItem = (
    packageDetail: Package,
    personType: PackageItemPersonType = PackageItemPersonType.Adult
): PackageItem | undefined => {
    return packageDetail.packageItems
        .filter((packageItem) => packageItem.person_type === personType)
        .find((packageItem) => {
            if (packageItem.type === PackageItemItemType.Upsell) {
                return packageItem.extra.extra_heat
            }
            return false
        })
}

export const minimumPackagePrice = (
    packageDetail: Package,
    personType: PackageItemPersonType = PackageItemPersonType.Adult
): PackageItem => {
    const filteredPackageByPersonType = packageDetail.packageItems
        .filter((packageItem) => packageItem.person_type === personType)
        .filter((packageItem) => packageItem.type === PackageItemItemType.PackageItem)
    const minimum = Math.min(...filteredPackageByPersonType.map((packageItem) => packageItem.price))
    const resolvedPackageItem: PackageItem = <PackageItem>(
        filteredPackageByPersonType.find((item) => item.price == minimum)
    )
    return resolvedPackageItem
}

export const isWeekdayDiscountedPackageItem = (weekDay: number, packageItem: PackageItem): boolean => {
    if (!packageItem.extra.includeWeekDays) {
        return false
    }
    const days = packageItem.extra.includeWeekDays.filter((includeWeekDay) => includeWeekDay === weekDay)
    return days.length > 0
}

export const packageHasUpsellItems = (Package: Package): boolean => {
    return (
        Package.packageItems.filter(
            (packageItem: PackageItem): boolean => packageItem.type === PackageItemItemType.Upsell
        ).length > 0
    )
}

export const packageMinimalDate = (Package: Package): Date => {
    const today = new Date()
    if (Package.extra.releaseDate) {
        const releaseDate = new Date(Package.extra.releaseDate)
        if (today.getTime() > releaseDate.getTime()) {
            return today
        }
        return new Date(Package.extra.releaseDate)
    }
    if (Package.extra.minDaysFromNow) {
        return new Date(Dates.addDays(today.toString(), Package.extra.minDaysFromNow).toString())
    }
    return today
}

export const packageCategoryMinimalDate = (packageCategoryEntity: PackageCategoryEntity): Date => {
    const today = new Date()

    if (packageCategoryEntity.extra?.releaseDate) {
        return new Date(packageCategoryEntity.extra.releaseDate)
    }

    if (
        packageCategoryEntity.id == PackageCategory.FerienPassB ||
        packageCategoryEntity.id == PackageCategory.FerienPass
    ) {
        return new Date(Dates.addDays(today.toString(), 1).toString())
    }
    if (packageCategoryEntity.extra?.minDaysFromNow) {
        return new Date(Dates.addDays(today.toString(), packageCategoryEntity.extra.minDaysFromNow).toString())
    }

    return today
}

export const packageMaximumDate = (Package: Package): Date => {
    const today = new Date()

    if (Package.extra.onlyOnDate) {
        return new Date(Package.extra.onlyOnDate)
    }

    if (Package.extra.maxDaysFromNow) {
        return new Date(Dates.addDays(today.toString(), Package.extra.maxDaysFromNow).toString())
    }
    return today
}

export const packageCategoryMaximumDate = (packageCategoryEntity: PackageCategoryEntity): Date => {
    const today = new Date()

    if (!packageCategoryEntity.extra) {
        return today
    }
    if (packageCategoryEntity.extra.onlyOnDate) {
        return new Date(packageCategoryEntity.extra.onlyOnDate)
    }

    if (packageCategoryEntity.extra.maxDaysFromNow) {
        return new Date(Dates.addDays(today.toString(), packageCategoryEntity.extra.maxDaysFromNow).toString())
    }
    //fallback
    return today
}

/**
 * Stable function that returns weekday numbers from filterByWeekDay
 *
 * @param Package
 */
const getWeekDaysFromPackageExtra = (Package: Package) => {
    const weekDays: number[] = Package.extra.filterByWeekDay
        ? Package.extra.filterByWeekDay.map((packageExtraFilter) => {
              const weekDay = packageExtraFilter.weekDay
              if (weekDay === 7) {
                  return 0
              }
              return weekDay
          })
        : []

    return weekDays
}

/**
 * Stable function that returns weekday numbers from PackageCategory - hard predefined
 *
 * @param {PackageCategory} packageCategory
 */
const getWeekDaysFromPackageCategoryExtra = (packageCategory: PackageCategory): number[] => {
    const weekDays: (packageCategory: PackageCategory) => number[] = (packageCategory: PackageCategory) => {
        switch (packageCategory) {
            case PackageCategory.Action:
                return [0, 1, 2, 3]
            case PackageCategory.FerienPass:
                return [1, 2, 3, 4, 5]
            case PackageCategory.FerienPassB:
                return [1, 2, 3, 4, 5]
            default:
                return []
        }
    }

    return weekDays(packageCategory)
}

/**
 * Stable function creates arrays of days that are included
 * @param date
 * @param month
 * @param weekDays
 */
const createMonthDateArray = (date: Date, month: number, weekDays: number[]) => {
    const days: Date[] = []
    while (date.getMonth() === month) {
        //repeat for the whole month from 1st date until end of month
        if (weekDays.includes(date.getDay())) {
            days.push(new Date(date))
        }
        date.setDate(date.getDate() + 1)
    }
    return days
}

const generateConsecutiveMonths = (dateEntry: Date, monthIndex: number, weekDays: number[]) => {
    // Validate the input parameters
    if (!(dateEntry instanceof Date)) {
        throw new Error('dateEntry should be a valid Date object.')
    }
    if (typeof monthIndex !== 'number' || monthIndex < 0 || monthIndex > 11) {
        throw new Error('monthIndex should be an integer between 0 and 11.')
    }

    // Create an array to store the first days of consecutive months
    const firstDays: Date[] = []

    // Clone the dateEntry to avoid modifying the original object
    const currentDate = new Date(dateEntry)

    // Set the month of the currentDate to the specified monthIndex
    currentDate.setMonth(monthIndex)

    // Use a while loop to generate first days of 12 consecutive months
    let monthCount = 0
    while (monthCount < 12) {
        // Push the first day of the current month to the array
        const newDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1)
        firstDays.push(...createMonthDateArray(newDate, newDate.getMonth(), weekDays))

        // Move to the next month
        currentDate.setMonth(currentDate.getMonth() + 1)

        monthCount++
    }
    return firstDays
}

export const packageExcludeDates = (
    Package: Package,
    month: number,
    type: 'exclude' | 'include' = 'exclude'
): Date[] => {
    const weekDays = getWeekDaysFromPackageExtra(Package)
    let days: Date[] = []
    const year = new Date().getFullYear()
    const date = new Date(year, month, 1)
    const allValid = [0, 1, 2, 3, 4, 5, 6]
    const filterWeekDays = allValid.filter((num) => {
        if (weekDays.length == 0) {
            return type === 'include'
        }
        return type === 'include' ? weekDays.includes(num) : !weekDays.includes(num)
    })

    if (filterWeekDays.length > 0) {
        days = generateConsecutiveMonths(date, month, filterWeekDays)
    }

    return days
}

export const useHallHolidays = (selectedPackageCategory: PackageCategory | null | undefined) => {
    const hallState = useTypedSelector((state) => state.hallState.hall)

    const getHallHolidays = (hall_id: number) => {
        if (!hallState?.futureHallHolidays) {
            return []
        }

        return hallState.futureHallHolidays
            .filter((hallHoliday) => hallHoliday.hall_id === hall_id)
            .map((hallHoliday) => {
                return {
                    date: hallHoliday.date,
                    type: hallHoliday.type,
                    dayOfWeek: new Date(hallHoliday.date).getDay(),
                }
            })
    }

    const getHallOpeningHoursEntries = (hall_id: number) => {
        if (!hallState?.hallOpeningHoursEntries) {
            return []
        }
        return hallState.hallOpeningHoursEntries
            .filter((hallOpeningHoursEntry: HallOpeningHourEntries) => hallOpeningHoursEntry.hall_id === hall_id)
            .map((hallOpeningHoursEntry) => {
                const isOpen = hallOpeningHoursEntry.from_hours !== hallOpeningHoursEntry.to_hours
                return {
                    date: hallOpeningHoursEntry.date,
                    type: isOpen ? 'open' : 'closed',
                    family_action: hallOpeningHoursEntry.family_ticket,
                    dayOfWeek: new Date(hallOpeningHoursEntry.date).getDay(),
                }
            })
    }

    const includedDays = getHallOpeningHoursEntries(1).filter((day) => {
        if (selectedPackageCategory && selectedPackageCategory === PackageCategory.FerienPass) {
            return day.type === 'open' && [1, 2, 3, 4, 5].includes(new Date(day.date).getDay())
        }
        if (selectedPackageCategory && selectedPackageCategory === PackageCategory.FerienPassB) {
            if (new Date(day.date).getMonth() == 7 && new Date(day.date).getDate() == 1) {
                return false
            }
            return day.type === 'open' && [1, 2, 3, 4, 5].includes(new Date(day.date).getDay())
        }
        if (selectedPackageCategory && selectedPackageCategory === PackageCategory.Action) {
            return day.type === 'open' && day.family_action
        }
        return day.type === 'open'
    })
    // console.log("includedDays", includedDays);
    const formatDate = (date: string | Date | number) => {
        const d = new Date(date)
        return (
            d.getFullYear() +
            '-' +
            (d.getMonth() + 1).toString().padStart(2, '0') +
            '-' +
            d.getDate().toString().padStart(2, '0')
        )
    }

    const includedDates = includedDays.map((day) => {
        const datum = new Date(formatDate(day.date))
        datum.setHours(0)
        return datum
    })

    const filteredExcludedDays = (excludeDays: any[]) =>
        excludeDays.filter((excludedDay) => {
            return !includedDays.find((includedDay) => {
                return formatDate(excludedDay) === formatDate(includedDay.date)
            })
        })

    return {
        formatDate,
        includedDays,
        includedDates,
        filteredExcludedDays,
        getHallHolidays,
    }
}

export const packageCategoryExcludeDates = (
    packageCategory: PackageCategory,
    month: number,
    type: 'exclude' | 'include' = 'exclude'
): Date[] => {
    const weekDays = getWeekDaysFromPackageCategoryExtra(packageCategory)
    let days: Date[] = []
    const year = new Date().getFullYear()
    const date = new Date(year, month, 1)
    const allValid = [0, 1, 2, 3, 4, 5, 6]
    const filterWeekDays = allValid.filter((num) => {
        if (weekDays.length == 0) {
            return type === 'include'
        }
        return type === 'include' ? weekDays.includes(num) : !weekDays.includes(num)
    })

    if (filterWeekDays.length > 0) {
        days = generateConsecutiveMonths(date, month, filterWeekDays)
    }

    return days
}

export const getPackageRespectsTimeslotSize = (Package: Package): boolean => {
    const category = Package.category
    switch (category) {
        case PackageCategory.Event:
            return false
        case PackageCategory.Main:
            return false
        case PackageCategory.General:
            return true
        case PackageCategory.Profile:
            return true
        case PackageCategory.Season:
            return true
    }

    return true
}

export const getPackageTimeslotsCount = (
    Package: Package,
    personType: PackageItemPersonType = PackageItemPersonType.Adult
): number => {
    const packageCount = Package.packageItems
        .filter((packageItem: PackageItem) => packageItem.person_type === personType)
        .reduce((prev: number, cur: PackageItem) => {
            const packageItemTimeslotsCount = cur.article.race_count_total
            if (!prev) {
                return packageItemTimeslotsCount
            }
            return prev + packageItemTimeslotsCount
        }, 0)
    return packageCount
}

export const isYearly = (Package: Package) => Package.category === PackageCategory.Season
export const isThreeTwo = (Package: Package) => Package.category === PackageCategory.ThreeTwo
export const isGiftCard = (Package: Package) => Package.category === PackageCategory.Giftcard
export const isEvent = (Package: Package) => Package.category === PackageCategory.Event
export const isAction = (Package: Package) => Package.category === PackageCategory.Action
export const isGroupTypeChildren = (Package: Package) =>
    Package.extra.groupType && Package.extra.groupType.includes(GroupTypeId.Children)

export const packagesCartContainsUpsells = (packagesCart: PackagesCart, upsellOptionType?: boolean): boolean => {
    if (
        typeof packagesCart.packagesCartItems?.filter(
            (packagesCartItem) => packagesCartItem.packageItem?.type === PackageItemItemType.Upsell
        ).length === 'number'
    ) {
        if (typeof upsellOptionType !== 'undefined') {
            return (
                packagesCart.packagesCartItems
                    ?.filter((packagesCartItem) => packagesCartItem.packageItem?.type === PackageItemItemType.Upsell)
                    .filter((packagesCartItem) => {
                        if (
                            typeof packagesCartItem !== 'undefined' &&
                            typeof packagesCartItem.packageItem !== 'undefined'
                        ) {
                            // @ts-ignore
                            return packagesCartItem?.packageItem?.extra.upsell_option >= 0
                        }
                        return false
                    }).length > 0
            )
        }

        return (
            packagesCart.packagesCartItems?.filter(
                (packagesCartItem) => packagesCartItem.packageItem?.type === PackageItemItemType.Upsell
            ).length > 0
        )
    }
    return false
}

export const getPersonsTypeFraction = (totalPersons: number, limit: number) =>
    totalPersons <= limit ? totalPersons / limit : 1

/**
 *
 * @param packageRow
 * @param eligibleVoucher
 * @return number
 */
export const voucherDiscount = (packageRow: Package, eligibleVoucher: VoucherItem): number => {
    const { getVoucherType } = useVoucherTypeByArticle()
    let discountedPrice: number = packageRow.price
    const packageArticle = packageRow.packageItems.map((packageItem) => packageItem.article)
    const voucherDiscountType = getVoucherType(eligibleVoucher).type

    if (!IS_PROD) {
        // console.log(voucherDiscountType);
    }

    switch (voucherDiscountType) {
        case VoucherDiscountType.Percent:
            return (discountedPrice = packageRow.price * ((100 - getVoucherType(eligibleVoucher).value) / 100))
        case VoucherDiscountType.Race: {
            const voucherArticleMatchRace = packageArticle.find((article) => {
                return article.race_count_total == eligibleVoucher.article.race_count_total
            })
            if (!IS_PROD) {
                console.log(voucherArticleMatchRace)
            }
            return (discountedPrice = voucherArticleMatchRace
                ? packageRow.price - voucherArticleMatchRace.article_value
                : packageRow.price)
        }
        case VoucherDiscountType.Activity: {
            const voucherArticleMatchActivity = packageArticle.find((article) => {
                // @ts-ignore // already matched with if(eligibleVoucher)
                return article.article_id === eligibleVoucher.article.article_id
            })
            return (discountedPrice = voucherArticleMatchActivity
                ? packageRow.price - voucherArticleMatchActivity.article_value
                : packageRow.price)
        }
        default:
            return discountedPrice
    }
}

export const isExclusiveAtSpreitenbach = (Package: Package, hall: Hall) =>
    hall.hall_code === HallCode.Spreitenbach && Package.rewrite_url === 'exclusive'

export const getModuloMinutesByPackageAndHall = (Package: Package, hall: Hall) => {
    if (isGroupTypeChildren(Package)) {
        return 60
    }

    if (isExclusiveAtSpreitenbach(Package, hall)) {
        return 30
    }

    return hall.default_bookable_intervals ? hall.default_bookable_intervals : 60
}

const getUpsellsForPackage = (Package: Package) => {
    return Package.packageItems
        .filter((packageItem) => {
            if (packageItem.type === PackageItemItemType.Upsell) {
                return true
            }
            return false
        })
        .map((packageItem) => packageItem.id)
}

export const useClearUpsells = (bookingProcess: BookingProcess) => {
    const dispatch = useAppDispatch()
    const selectedUpsells = bookingProcess.upsell.map((packageUpsell) => packageUpsell.packageItem.id)

    const clearInvalidUpsells = (): any[] => {
        if (!bookingProcess.package) {
            return []
        }

        const upsellsToNotClear = getUpsellsForPackage(bookingProcess.package).filter((upsellItem) =>
            selectedUpsells.includes(upsellItem)
        )
        const upsellsCleared = bookingProcess.upsell.filter(
            (packageUpsell) => !upsellsToNotClear.includes(packageUpsell.packageItem.id)
        )
        return upsellsCleared.map((upsell) => {
            return dispatch(BookingAction.removeUpsell(upsell))
        })
    }

    return {
        clearInvalidUpsells,
    }
}

export const normalizeDate = (date: Date): Date => {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate())
}

export const getSelectedUpsellPackages = (selectedUpsells: PackageUpsell[]): PackageUpsellItem[] =>
    selectedUpsells
        .filter((upsell) => !upsell.packageItem.creditPackage)
        .map((upsell) => {
            return {
                package_item_id: upsell.packageItem.id,
                quantity: +upsell.count,
                upsell_option: upsell.upsell_option,
            }
        })

export const getSelectedCreditPackages = (selectedUpsells: PackageUpsell[]): PackageCreditItem[] =>
    selectedUpsells
        .filter((upsell) => !!upsell.packageItem.creditPackage)
        .map((upsell) => {
            return {
                package_item_id: upsell.packageItem.id,
                quantity: +upsell.count,
            }
        })
//
export const getAvailableDatesForCategory = (
    selectedCategory: PackageCategory,
    packageEntity: PackageCategoryEntity,
    excludedDates: Date[],
    includedDays: Date[]
) => {
    const maxDate = addMonths(new Date(), MAX_MONTH_COUNT_FOR_AVAILABLE_DATES)
    const lastMidnight = new Date()
    lastMidnight.setHours(0, 0, 0, 0)
    const minimalDatePreset = new Date(Date.now() + DEFAULT_RESERVATION_DAYS_FROM_NOW * 24 * 60 * 60 * 1000)
    const minimalDate =
        selectedCategory === null ? new Date(minimalDatePreset) : packageCategoryMinimalDate(packageEntity)

    minimalDate.setHours(0, 0, 0, 0)
    const allDatesFromThisMonth = packageCategoryExcludeDates(selectedCategory, minimalDate.getMonth(), 'include')
    allDatesFromThisMonth.push(...includedDays)

    const uniqueDates = allDatesFromThisMonth.reduce((acc: Date[], date) => {
        const duplicate = acc.find((a) => date.getTime() === a.getTime())
        if (!duplicate) {
            acc.push(date)
        }
        return acc
    }, [])

    const sorted = uniqueDates.sort((a: Date, b: Date) => (a.getTime() >= b.getTime() ? 1 : -1))
    return sorted.filter((date: Date) => {
        const exclDateExists = excludedDates.some((exclDate) => date.toDateString() === exclDate.toDateString())

        if (exclDateExists || date.getTime() < minimalDate.getTime()) {
            return false
        }

        if (date && maxDate && normalizeDate(maxDate) < normalizeDate(date)) {
            return false
        }

        return date.getTime() >= lastMidnight.getTime()
    })
}

export const getAvailableDates = (selectedPackage: Package, excludedDates: Date[]) => {
    const lastMidnight = new Date()
    lastMidnight.setHours(0, 0, 0, 0) // Midnight today

    const minimalDate = packageMinimalDate(selectedPackage)
    minimalDate.setHours(0, 0, 0, 0) // Start of the minimal date

    const allDatesFromThisMonth = packageExcludeDates(selectedPackage, minimalDate.getMonth(), 'include')
    const maximumDate = packageMaximumDate(selectedPackage)
    maximumDate.setHours(23, 59, 59, 999) // End of the maximum date

    // Set excluded dates to midnight for accurate comparison
    const excludedDateSet = new Set(
        excludedDates.map((exclDate) => {
            const date = new Date(exclDate)
            date.setHours(0, 0, 0, 0) // Set to midnight
            return date.toISOString().split('T')[0] // Compare as YYYY-MM-DD
        })
    )

    return allDatesFromThisMonth.filter((date) => {
        const dateStr = date.toISOString().split('T')[0] // Convert to YYYY-MM-DD for comparison
        if (excludedDateSet.has(dateStr)) {
            return false // Excluded date
        }

        const time = date.getTime()
        return time >= minimalDate.getTime() && time <= maximumDate.getTime() && time >= lastMidnight.getTime()
    })
}

export const getCurrentDateIndex = (availableDates: Date[], filter: BookingFilter) => {
    return availableDates.findIndex((date) => {
        const filterDateMidnight = new Date(filter.date)
        filterDateMidnight.setHours(0, 0, 0, 0)
        return date.getTime() === filterDateMidnight.getTime()
    })
}

export const fakeTimeslotMinute = (selectedPackage: Package | null) =>
    selectedPackage?.category === PackageCategory.Event && DEFAULT_HALL !== HallCode.ArboPark

export const getTimeslotsAvailable = (flattenedTimeslots: Timeslot[], _hall: Hall | null) => {
    return flattenedTimeslots
}

export const getPossibleSlotIndex = (
    timeslot: Timeslot,
    type: 'first' | 'last',
    timeslotsAvailable: Timeslot[],
    selectedPackage: Package | null
) => {
    const selectedSlotIndex = timeslotsAvailable.findIndex((timeslotAvailable) => timeslotAvailable.id === timeslot.id)

    if (selectedSlotIndex === -1) {
        return null
    }

    const selectedSlotTime = new Date(timeslotsAvailable[selectedSlotIndex].start_at)
    let hoursToAdd: number

    if (type === 'last') {
        hoursToAdd = selectedPackage?.extra.maxHoursForTimeslotSpan || 24
    } else if (type === 'first') {
        hoursToAdd = selectedPackage?.extra.maxHoursForTimeslotSpan
            ? +1 - selectedPackage?.extra.maxHoursForTimeslotSpan
            : 24
    } else {
        throw new Error('Invalid type parameter. Use "first" or "last".')
    }

    selectedSlotTime.setHours(selectedSlotTime.getHours() + hoursToAdd)

    const comparisonFunction =
        type === 'last'
            ? (timeslot: Timeslot) => new Date(timeslot.start_at).getTime() <= selectedSlotTime.getTime()
            : (timeslot: Timeslot) => new Date(timeslot.start_at).getTime() >= selectedSlotTime.getTime()

    const filteredTimeslots = timeslotsAvailable.filter(comparisonFunction)
    const possibleSlotIndex = type === 'last' ? filteredTimeslots.length - 1 : 0
    return possibleSlotIndex >= 0 ? timeslotsAvailable.indexOf(filteredTimeslots[possibleSlotIndex]) : null
}

export const isKartingPackageItem = (packageItem: PackageItem, activities: Activity[]) => {
    const foundActivity = activities.find((activity) => activity.id === packageItem.activity_id)
    return foundActivity?.type === ActivityType.Karting
}

export const packageItemForPersonType = (
    personType: PackageItemPersonType,
    packageItems: PackageItem[]
): PackageItem => {
    const item = findPackageItemByPersonType(packageItems, personType)
    return item as PackageItem
}

export const packagesCartItemsOutstanding = (packagesCartItems: PackagesCartItem[]) => {
    return packagesCartItems.filter((packagesCartItem: PackagesCartItem) => {
        if (packagesCartItem.packageItem?.type === PackageItemItemType.BasePrice) {
            return false
        }
        if (packagesCartItem.packageItem?.activity_id === null) {
            return true
        }
        return false
    })
}

export const filterAccessItemSlots = (items: PackagesCartItemSlot[]) => {
    const filteredType = (packageItem: PackageItem): boolean => {
        if (packageItem.article.ticket_type === ArticleTicketType.Access) {
            return SHOW_ITEM_SLOT_RANGE_ONLY
        }
        return false
    }
    return items.filter((item, index) => {
        if (SHOW_ITEM_SLOT_RANGE_ONLY) {
            //show only first
            if (SHOW_ONLY_RESERVATION_START) {
                if (index === 0) {
                    return filteredType(item.packageItem)
                }
                return false
            }
            if (index === 0 || index === items.length - 1) {
                return filteredType(item.packageItem)
            }
            return false
        } else {
            return true
        }
    })
}

export const getPackageCategoryName = (categorySlug: string) => {
    switch (categorySlug) {
        case PackageCategory.EventM:
            return 'Mega'
        case PackageCategory.EventS:
            return 'Fun'
        case PackageCategory.EventL:
            return 'Ultra'
        case PackageCategory.Giftcard:
            return 'Gift'
        case PackageCategory.Others:
        case PackageCategory.General:
            return 'Standard'
        case PackageCategory.Action:
            return 'Family'
        case PackageCategory.School:
            return 'School'
        case PackageCategory.SchoolTrophy:
            return 'School Trophy'
        case PackageCategory.PolterabendMegaFun:
            return 'Polterabend Mega Fun'
        case PackageCategory.PolterabendNonPlusUltra:
            return 'Polterabend Non Plus Ultra'
        case PackageCategory.MegaFun:
        case PackageCategory.EventMegaFun:
            return 'Mega Fun'
        case PackageCategory.EventNonPlusUltra:
            return 'Non Plus Ultra'
        // case PackageCategory.NonPlusUltraFun:
        //     return 'Non Plus Ultra'
        // case PackageCategory.NonPlusUltraExtraFun:
        //     return 'Non Plus Ultra Extra Fun'
        // case PackageCategory.KindergeburtstagMegaFun:
        //     return 'Kindergeburtstag Mega Fun'
        // case PackageCategory.KindergeburtstagNonPlusUltraFun:
        //     return 'Kindergeburtstag Non Plus Ultra Fun'
        // case PackageCategory.PartyMegaFun:
        //     return 'Party Mega Fun'
        // case PackageCategory.PartyNonPlusUltraFun:
        //     return 'Party Non Plus Ultra'
        // case PackageCategory.PartyNonPlusUltraExtraFun:
        //     return 'Party Non Plus Ultra Extra Fun'
        case PackageCategory.FerienPass:
            return 'FerienPass'
        case PackageCategory.FerienPassB:
            return 'FerienPass No ADults'
        case PackageCategory.Kinder:
            return 'Kinder'
        default:
            return '---'
    }
}

export const getPackageCategoryClassName = (categorySlug: PackageCategory) => {
    switch (categorySlug) {
        case PackageCategory.EventMegaFun:
            return 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-100'
        case PackageCategory.EventNonPlusUltra:
            return 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-100'
        case PackageCategory.PolterabendMegaFun:
            return 'bg-violet-100 text-violet-800 dark:bg-violet-900 dark:text-violet-100'
        case PackageCategory.PolterabendNonPlusUltra:
            return 'bg-amber-300 text-amber-800 dark:bg-amber-900 dark:text-amber-100'
        case PackageCategory.Giftcard:
            return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-100'
        case PackageCategory.Others:
        case PackageCategory.General:
            return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-100'
        case PackageCategory.Action:
            return 'bg-rose-100 text-rose-800 dark:bg-rose-900 dark:text-rose-100'
        case PackageCategory.SchoolTrophy:
            return 'bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-100'
        case PackageCategory.School:
            return 'bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-100'
        case PackageCategory.MegaFunSchool:
            return 'bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-100'
        case PackageCategory.FerienPass:
            return 'bg-sky-100 text-sky-800 dark:bg-sky-900 dark:text-sky-100'
        case PackageCategory.FerienPassB:
            return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-100'
        case PackageCategory.Kinder:
            return 'bg-pink-100 text-pink-800 dark:bg-pink-900 dark:text-pink-100'
        default:
            return 'bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-100'
    }
}

export const hasKartingPackageItems = (packageItems?: PackagesItemsPersons | null): boolean => {
    if (!packageItems) {
        return false
    }

    return [...packageItems.adult, ...packageItems.child].some((item) => item.karting)
}

export const usePackageItemKartingOptions = () => {
    const selectedPackageItems = useTypedSelector((state) => state.bookingState.bookingProcess.selectedPackageItems)
    const dispatch = useAppDispatch()
    const filter = useTypedSelector((state) => state.bookingState.bookingProcess.filter)

    const availablePackageItems = useTypedSelector((state) => state.bookingState.bookingProcess.availablePackageItems)

    const peopleCount: number = filter.adults + filter.children

    /**
     * Retrieves a saved redux state package item based on the person index.
     * If the saved item is found, updates the selected package item,
     * withKarting value, and chosen duration based on the saved item.
     *
     * @param {number} personIndex - The index of the person associated with the saved package item
     * @param personType
     */
    const getSavedPackagesItemByPerson = (personIndex: number, personType: PackageItemPersonType) => {
        const savedItemIndex = selectedPackageItems.findIndex(
            (item) => item.person_type == personType && item.personIndex == personIndex
        )

        if (savedItemIndex !== -1 && availablePackageItems) {
            const savedItem = selectedPackageItems[savedItemIndex]
            const foundItemIndex = (availablePackageItems.packageItems as any)[savedItem.person_type]
                ? (availablePackageItems.packageItems as any)[savedItem.person_type].findIndex(
                      (item: PackagesItemsPerson) => item.id == savedItem.id
                  )
                : -1
            if (foundItemIndex !== -1 && personIndex == savedItem.personIndex) {
                return (availablePackageItems.packageItems as any)[savedItem.person_type][foundItemIndex]
            }
        } else {
            console.log('setting to default, should never happen because ', personIndex, personType)
            // Default values if no saved package item is found (likely for children or new entries)
            return null
        }
    }

    /**
     * Calculates the least possible duration based on the provided input.
     *
     * @function
     * @name getLeastPossibleDuration
     * @param {number} [duration=240] - The initial duration to evaluate. Defaults to 240 if not provided.
     * @param {boolean} [isKarting=true] - The initial isKarting default value. Will be deprecated soon
     * @returns {boolean} - Returns `true` if no suitable package item combination is found; otherwise, `false`.
     */
    const getLeastPossibleDuration = (duration: number = 240, isKarting: boolean = true): boolean => {
        const packagesItemsPerson: PackagesItemsPerson | undefined = getPackageItemForCombination(
            duration,
            isKarting,
            PackageItemPersonType.Adult
        )
        return packagesItemsPerson === undefined
    }

    /**
     * Sets default selected package items based on the number of people and selected package items.
     * Each person in the specified count gets a default package item based on their person type.
     * If a specific package item is selected for a person, that item is returned; otherwise, a default item is generated.
     */
    const setDefaultPackagesItems = () => {
        const def: PackagesItemByPerson[] = Array.from({ length: peopleCount }, (_, index) => {
            // Update condition to properly differentiate between adults and children
            const personType: PackageItemPersonType =
                index < filter.adults ? PackageItemPersonType.Adult : PackageItemPersonType.Child
            const selectedPackageItemForIndex = selectedPackageItems.findIndex((item) => {
                return item.personIndex == index && item.person_type == personType
            })

            // Assign default values when no selected item exists
            return {
                personIndex: personType === PackageItemPersonType.Child ? 100 + (index - filter.adults) : index,
                id: selectedPackageItemForIndex !== -1 ? selectedPackageItems[selectedPackageItemForIndex].id : null, // Avoid null, use default
                person_type: personType,
                quantity: 1,
            }
        })
        console.log(def)
        dispatch(BookingAction.setSelectedPackageItems(def))
    }

    const setPackageItemHandler = (
        personIndex: number,
        id: number | null,
        person_type: PackageItemPersonType,
        quantity: number
    ) => {
        dispatch(
            BookingAction.setSelectedPackageItem({
                id: id,
                personIndex: personIndex,
                person_type: person_type,
                quantity: quantity,
            })
        )
    }

    /**
     * Retrieves a package item based on the provided duration, karting option, and person type
     *
     * @param {number} duration - The duration of the package item
     * @param {boolean} isKarting - Indicates if karting is included in the package item
     * @param {PackageItemPersonType} personType - The type of person for the package item
     * @returns {undefined | PackagesItemsPerson} - The package item matching the criteria, or undefined if not found
     */
    const getPackageItemForCombination = (
        duration: number | undefined,
        isKarting: boolean,
        personType: PackageItemPersonType
    ): undefined | PackagesItemsPerson => {
        if (!availablePackageItems) {
            return
        }
        const packageItems = (availablePackageItems.packageItems as any)[personType]
        const packageItemIndex = packageItems
            ? packageItems.findIndex(
                  (item: PackagesItemsPerson) => item.duration === duration && item.karting === isKarting
              )
            : -1
        if (packageItemIndex == -1) {
            return
        }
        return (availablePackageItems.packageItems as any)[personType][packageItemIndex]
    }

    const manageResetStates = () => {
        const adultsCountSaved = selectedPackageItems.filter(
            (item) => item.person_type === PackageItemPersonType.Adult
        ).length
        const childrenCountSaved = selectedPackageItems.filter(
            (item) => item.person_type === PackageItemPersonType.Child
        ).length
        if (filter.adults + filter.children == 0) {
            resetDefaultPackagesItems().then((_r) => {
                setDefaultPackagesItems()
            })
        }

        if (adultsCountSaved !== filter.adults || childrenCountSaved !== filter.children) {
            resetDefaultPackagesItems().then((_r) => {
                setDefaultPackagesItems()
            })
        }
    }

    const getTotalPrice = () => {
        return selectedPackageItems.reduce((total, cartItem) => {
            const packagesItemByPerson = getSavedPackagesItemByPerson(cartItem.personIndex, cartItem.person_type)
            if (filter.category === PackageCategory.FerienPass) {
                if (cartItem.person_type === PackageItemPersonType.Adult && cartItem.personIndex === 0) {
                    return total
                }
            }
            if (!packagesItemByPerson) {
                return total
            }
            if (!total) {
                return packagesItemByPerson?.price
            }
            return total + packagesItemByPerson?.price
        }, 0)
    }

    const resetDefaultPackagesItems = async () => {
        await dispatch(BookingAction.resetSelectedPackageItems())
    }

    const getFilledCount = () => {
        return selectedPackageItems.filter((cartItem) => cartItem.id !== null).length
    }

    const getBookingPackageItems = () => {
        const validIds = selectedPackageItems.filter((cartItem) => cartItem.id)
        return validIds.map((cartItem) => cartItem.id) as number[]
    }
    const getBookingPackageItemsForEvent = () => {
        const quantityByItemId: Record<number, number> = {}

        selectedPackageItems.forEach((cartItem) => {
            if (!cartItem.id) {
                return
            }

            const currentQuantity = quantityByItemId[cartItem.id] ?? 0
            quantityByItemId[cartItem.id] = currentQuantity + cartItem.quantity
        })

        const result = Object.entries(quantityByItemId).map(([itemId, quantity]) => ({ [Number(itemId)]: quantity }))

        return result
    }

    const getBookingPackageItemsWithQuantity = () => {
        const validIds = selectedPackageItems.filter((cartItem) => cartItem.id)
        return validIds.map((cartItem) => cartItem.id) as number[]
    }

    getBookingPackageItemsWithQuantity()

    return {
        getLeastPossibleDuration,
        getSavedPackagesItemByPerson,
        setPackageItemHandler,
        setDefaultPackagesItems,
        manageResetStates,
        getPackageItemForCombination,
        getTotalPrice,
        getFilledCount,
        getBookingPackageItems,
        getBookingPackageItemsForEvent,
    }
}

export const usePackagesCart = (packagesCart: PackagesCart) => {
    const packagesCartItems =
        packagesCart && 'packagesCartItems' in packagesCart
            ? [...packagesCart.packagesCartItems].sort((a, b) =>
                  a.packageItem!.article_id > b.packageItem!.article_id ? 1 : -1
              )
            : []

    const packagesCartItemVouchers =
        packagesCart && 'packagesCartItemVouchers' in packagesCart ? packagesCart?.packagesCartItemVouchers : []

    const filterItemsByType = (itemType: PackageItemItemType): PackagesCartItem[] =>
        packagesCartItems.filter((packagesCartItem) => packagesCartItem.packageItem?.type == itemType)

    const priceByType = (itemType: PackageItemItemType): number =>
        filterItemsByType(itemType).reduce((prev, cur: PackagesCartItem) => prev + (cur.price || 0), 0)

    return {
        packagesCartItems,
        packagesCartItemVouchers,
        filterItemsByType,
        priceByType,
    }
}

export const usePackagesCartItemSorting = () => {
    const getCartItemSortRank = (item: PackagesCartItem): number => {
        const packageItem = item.packageItem
        if (!packageItem) {
            return 99
        }

        if (packageItem.type === PackageItemItemType.PackageItem) {
            return 0
        }

        const groupName = packageItem.extra?.groupName || ''
        if (groupName === 'Eintritt') {
            return 1
        }
        if (packageItem.type === PackageItemItemType.Credit || !!packageItem.creditPackage || groupName === 'Credits') {
            return 2
        }
        if (groupName === 'Competitions') {
            return 3
        }
        if (groupName === 'Apero and Snacks') {
            return 4
        }
        if (groupName === 'Warmes Essen') {
            return 5
        }

        return 6
    }

    const sortedItems = (items: PackagesCartItem[]) => {
        return items.slice().sort((a: PackagesCartItem, b: PackagesCartItem) => {
            const rankDiff = getCartItemSortRank(a) - getCartItemSortRank(b)
            if (rankDiff !== 0) {
                return rankDiff
            }

            const aPosition = a.packageItem?.position
            const bPosition = b.packageItem?.position
            if (typeof aPosition === 'number' && typeof bPosition === 'number') {
                if (aPosition === bPosition) {
                    return (a.packageItem?.article_id || 0) - (b.packageItem?.article_id || 0)
                }
                return aPosition - bPosition
            }
            if (typeof aPosition === 'number') return -1
            if (typeof bPosition === 'number') return 1
            return (a.packageItem?.article_id || 0) - (b.packageItem?.article_id || 0)
        })
    }

    return {
        sortedItems,
    }
}

export const filterPackageItemsUpsells = (packagesCartItems: PackagesCartItem[]) => {
    return packagesCartItems.filter((packageCartItem) => {
        if (packageCartItem.packageItem === null || typeof packageCartItem.packageItem === 'undefined') {
            return false
        }
        return packageCartItem.packageItem?.type === PackageItemItemType.Upsell
    })
}

export const getPackageCartPackage = (packagesCart: PackagesCart, packages: Package[]) => {
    const nonePackage = { name: '', price: 0 } as unknown as Package
    if (!packagesCart || !packages || packages.length === 0 || packagesCart?.packagesCartItems?.length === 0) {
        return nonePackage
    }
    const packageCartItems = packagesCart.packagesCartItems
    if (!packageCartItems) {
        return nonePackage
    }
    const packagesCartPackages = packageCartItems
        .filter((pci) => pci.packageItem.type === PackageItemItemType.PackageItem)
        .map((packageCartItem) => packageCartItem.packageItem.package_id)

    const uniquePackageId = packagesCartPackages && packagesCartPackages.filter((x, i, a) => a.indexOf(x) == i)
    const foundPackage = packages.find((packageState) => uniquePackageId.includes(packageState.id))
    if (typeof foundPackage == 'undefined') {
        console.log(uniquePackageId, packagesCart, 'has undefined')
    }
    if (typeof foundPackage == 'undefined') {
        return nonePackage
    }
    return foundPackage
}

export const getDriversCountByCategory = (category: PackageCategory) => {
    switch (category) {
        case PackageCategory.Event:
        case PackageCategory.EventS:
            return {
                adult: { minimum: 5, maximum: 50 },
                child: { minimum: 0, maximum: 0 },
                package: { minimum: 5, maximum: 50 },
            }
        case PackageCategory.EventM:
            return {
                adult: { minimum: 10, maximum: 50 },
                child: { minimum: 0, maximum: 0 },
                package: { minimum: 10, maximum: 50 },
            }
        case PackageCategory.EventL:
            return {
                adult: { minimum: 10, maximum: 50 },
                child: { minimum: 0, maximum: 0 },
                package: { minimum: 10, maximum: 50 },
            }
        case PackageCategory.EventMegaFun:
            return {
                adult: { minimum: 4, maximum: 100 },
                child: { minimum: 0, maximum: 0 },
                package: { minimum: 4, maximum: 100 },
            }
        case PackageCategory.EventNonPlusUltra:
            return {
                adult: { minimum: 8, maximum: 100 },
                child: { minimum: 0, maximum: 0 },
                package: { minimum: 8, maximum: 100 },
            }
        case PackageCategory.PolterabendMegaFun:
            return {
                adult: { minimum: 4, maximum: 100 },
                child: { minimum: 0, maximum: 0 },
                package: { minimum: 4, maximum: 100 },
            }
        case PackageCategory.PolterabendNonPlusUltra:
            return {
                adult: { minimum: 8, maximum: 100 },
                child: { minimum: 0, maximum: 0 },
                package: { minimum: 8, maximum: 100 },
            }

        case PackageCategory.Action:
            return {
                adult: { minimum: 1, maximum: 5 },
                child: { minimum: 1, maximum: 5 },
                package: { minimum: 2, maximum: 6 },
            }

        case PackageCategory.School:
            return {
                adult: { minimum: 1, maximum: 5 },
                child: { minimum: 9, maximum: 100 },
                package: { minimum: 10, maximum: 100 },
            }
        case PackageCategory.FerienPass:
            return {
                adult: { minimum: 1, maximum: 50 },
                child: { minimum: 4, maximum: 50 },
                package: { minimum: 5, maximum: 50 },
            }
        case PackageCategory.FerienPassB:
            return {
                adult: { minimum: 0, maximum: 0 },
                child: { minimum: 1, maximum: 50 },
                package: { minimum: 1, maximum: 50 },
            }
        case PackageCategory.Kinder:
            return {
                adult: { minimum: 1, maximum: 50 },
                child: { minimum: 4, maximum: 50 },
                package: { minimum: 5, maximum: 50 },
            }
        default:
            return {
                adult: { minimum: 1, maximum: 50 },
                child: { minimum: 0, maximum: 50 },
                package: { minimum: 1, maximum: 50 },
            }
    }
}

/**
 * Get package category options for dropdown select filters
 * @returns Array of options with label and value
 */
export const getPackageOptions = (): Array<{ label: string; value: string }> => {
    return ValidPackageCategory.map((value) => {
        return {
            label: getShortNameFromId(value),
            value,
        }
    })
}
